import { Link } from "react-router-dom"
import { config } from "../lib/config"

const base = config.apiUrl.replace(/\/$/, "")

const emitJsonExample = `{
  "ambiente": 2,
  "serie": 1,
  "external_id": "pedido-erp-12345",
  "natureza_operacao": "Venda",
  "dh_emi": "2026-09-21T14:30:00-03:00",
  "destinatario": {
    "nome": "Cliente Teste Homologacao",
    "cpf": "00000000000",
    "uf": "SP",
    "email": "cliente@example.com",
    "logradouro": "Rua Exemplo, 100"
  },
  "itens": [
    {
      "codigo": "SKU-001",
      "descricao": "Notebook 14 polegadas",
      "ncm": "84713012",
      "cfop": "5102",
      "unidade": "UN",
      "quantidade": 1,
      "valor_unitario": 3500.0
    },
    {
      "codigo": "SKU-002",
      "descricao": "Mouse sem fio",
      "ncm": "84716052",
      "cfop": "5102",
      "quantidade": 2,
      "valor_unitario": 89.9
    }
  ]
}`

const emitJsonResponseExample = `{
  "documento": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "external_id": "pedido-erp-12345",
    "status": "ENFILEIRADO",
    "ambiente": 2,
    "serie": 1,
    "numero": 42,
    "chave_acesso": null,
    "protocolo": null,
    "criado_em": "2026-09-21T17:30:00+00:00"
  },
  "idempotent_replay": false
}`

const emitJsonCnpjExample = `{
  "ambiente": 1,
  "serie": 1,
  "destinatario": {
    "nome": "Destinatario LTDA",
    "cnpj": "12345678000199",
    "uf": "SP"
  },
  "itens": [
    {
      "descricao": "Servico de consultoria",
      "ncm": "00000000",
      "cfop": "5933",
      "quantidade": 1,
      "valor_unitario": 1500.0
    }
  ]
}`

type DocSection = {
  title: string
  body?: string
  curl?: string
}

const sections: DocSection[] = [
  {
    title: "Autenticação",
    body: "Envie o header X-Api-Key com a chave gerada em Gestão → API keys (escopo da empresa).",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" "${base}/v1/empresas/{empresa_id}/nfe?page=1"`,
  },
  {
    title: "Dados do emitente",
    body: "CNPJ, razão social, fantasia, IE, CRT e endereço para fechar o <emit> e o município do fato gerador. País vem fixo Brasil (1058). Complemento é opcional. Se faltar logradouro, número, bairro, município ou CEP, a resposta é 422.",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/emitente"`,
  },
  {
    title: "Reservar número (nNF)",
    body: "Segura o próximo número da série no ambiente (1 produção, 2 homologação) para o external_id do pedido. O mesmo external_id devolve sempre o mesmo número. Outro pedido não recebe esse número. Se a nota não for autorizada, o número continua do pedido ou segue para inutilização — não volta para a fila.",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/reservar-numero" \\
  -d '{"serie":1,"ambiente":1,"external_id":"pedido-shopee-123"}'`,
  },
  {
    title: "Emitir a partir de XML (sem protocolo)",
    body: "Ordem: GET /emitente, POST /nfe/reservar-numero, monte o XML com essa série e nNF (sem assinatura), POST /nfe/xml com xml, external_id e reserva_id. O FiscalHub assina sem alterar campos e responde 202 com codigo_status_sefaz e motivo_status_sefaz (nulos até a SEFAZ). O webhook nfe.autorizado ou nfe.rejeitado traz cStat e xMotivo. Se a SEFAZ não for alcançada, o status fica FALHA_COMUNICACAO, sem código, e o webhook é nfe.erro. O mesmo external_id reenvia esse número. O download do XML autorizado devolve o nfeProc.",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/xml" \\
  -d '{"xml":"<NFe>...</NFe>","external_id":"pedido-shopee-123","reserva_id":"UUID-DA-RESERVA","ambiente":1}'`,
  },
  {
    title: "Importar nfeProc autorizado + DANFE",
    body: "Grava a nota como AUTORIZADO e devolve danfe_pdf_base64 na resposta (201).",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/importar" \\
  -d '{"xml":"<nfeProc>...</nfeProc>"}'`,
  },
  {
    title: "Consultar e listar",
    body: "Filtros: status, ambiente, serie, numero_inicial, numero_final, de, ate (YYYY-MM-DD).",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/nfe?ambiente=2&de=2026-01-01&ate=2026-12-31&serie=1&numero_inicial=1&numero_final=999"`,
  },
  {
    title: "Download XML / DANFE",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" -o nota.xml \\
  "${base}/v1/empresas/{empresa_id}/nfe/chave/{chave44}/xml"`,
  },
  {
    title: "ZIP em lote",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" -o lote.zip \\
  "${base}/v1/empresas/{empresa_id}/nfe/arquivos.zip?de=2026-01-01&ate=2026-03-31&incluir=ambos"`,
  },
  {
    title: "Cancelamento (202 → fila SEFAZ)",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/{documento_id}/cancelar" \\
  -d '{"justificativa":"Cancelamento por erro de digitacao no pedido"}'`,
  },
  {
    title: "Carta de correção",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/{documento_id}/cce" \\
  -d '{"correcao":"Correcao do endereco do destinatario conforme cadastro atualizado"}'`,
  },
  {
    title: "Inutilização de faixa",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe/inutilizacoes" \\
  -d '{"ambiente":2,"serie":1,"numero_inicial":10,"numero_final":20,"ano":2026,"justificativa":"Inutilizacao por falha de sequencia na numeracao"}'`,
  },
  {
    title: "Consultar situação na SEFAZ",
    curl: `curl -s -X POST -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/nfe/chave/{chave44}/sincronizar"`,
  },
  {
    title: "Resumo (dashboard / integração)",
    curl: `curl -s -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/nfe/resumo?periodo=30d"`,
  },
]

const CodeBlock = ({ children }: { children: string }) => (
  <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-slate-100">{children}</pre>
)

const EmitNfeJsonSection = () => (
  <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-6">
    <h2 className="text-xl font-semibold text-slate-900">Emitir NF-e (JSON)</h2>
    <p className="mt-2 text-sm text-slate-600">
      Venda interna simplificada (NF-e 4.00). Emitente cadastrado com <strong>UF SP</strong>. O worker monta o XML, assina
      com o certificado A1 (slot homologação ou produção conforme <code className="rounded bg-white px-1">ambiente</code>
      ) e envia à SEFAZ-SP. Schema OpenAPI: <strong>NfeEmitRequest</strong>.
    </p>

    <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-950">
      <p className="font-medium">Endpoint</p>
      <p className="mt-1 font-mono text-xs sm:text-sm">
        POST {base}/v1/empresas/{`{empresa_id}`}/nfe → <strong>202 Accepted</strong> (assíncrono)
      </p>
    </div>

    <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Fluxo recomendado</h3>
    <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
      <li>POST com JSON abaixo → guarde <code className="rounded bg-white px-1">documento.id</code> (e opcionalmente <code className="rounded bg-white px-1">external_id</code> para idempotência).</li>
      <li>Poll <code className="rounded bg-white px-1">GET /v1/empresas/{`{empresa_id}`}/nfe/{`{documento.id}`}</code> até <code className="rounded bg-white px-1">status</code> ser <strong>AUTORIZADO</strong>, <strong>REJEITADO</strong> ou <strong>FALHA_COMUNICACAO</strong>.</li>
      <li>Ou consulte por chave: <code className="rounded bg-white px-1">GET .../nfe/chave/{`{chave44}`}</code> quando <code className="rounded bg-white px-1">chave_acesso</code> estiver preenchida.</li>
      <li>Baixe arquivos: <code className="rounded bg-white px-1">GET .../nfe/{`{id}`}/xml</code> e <code className="rounded bg-white px-1">GET .../nfe/{`{id}`}/danfe</code>.</li>
      <li>Webhooks (se configurados): <code className="rounded bg-white px-1">nfe.enfileirado</code>, depois <code className="rounded bg-white px-1">nfe.autorizado</code>, <code className="rounded bg-white px-1">nfe.rejeitado</code> ou <code className="rounded bg-white px-1">nfe.erro</code>.</li>
    </ol>

    <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Pré-requisitos</h3>
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
      <li>API key com permissão de emissão (papel emissor).</li>
      <li>Certificado A1 no painel → Certificados (slot <strong>Homologação</strong> se <code className="rounded bg-white px-1">ambiente: 2</code>, <strong>Produção</strong> se <code className="rounded bg-white px-1">ambiente: 1</code>).</li>
      <li>Worker em execução (fila Postgres).</li>
      <li>Notas com layout fiscal completo (vários impostos, transporte, etc.): prefira <strong>POST /nfe/xml</strong> com XML do ERP.</li>
    </ul>

    <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Campos do body</h3>
    <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr>
            <th className="px-3 py-2 font-medium">Campo</th>
            <th className="px-3 py-2 font-medium">Obrig.</th>
            <th className="px-3 py-2 font-medium">Descrição</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-slate-700">
          <tr>
            <td className="px-3 py-2 font-mono text-xs">ambiente</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">
              <code className="rounded bg-slate-100 px-1">1</code> produção, <code className="rounded bg-slate-100 px-1">2</code> homologação (padrão)
            </td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">serie</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Série 1–999 (padrão 1)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">numero</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Se omitido, reserva o próximo número da série</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">external_id</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Idempotência: reenvio com o mesmo valor devolve o documento já criado</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">natureza_operacao</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Texto da operação (padrão &quot;Venda&quot;)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">dh_emi</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Data/hora ISO 8601; padrão = agora (America/Sao_Paulo)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.nome</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">Nome ou razão social</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.cpf</td>
            <td className="px-3 py-2">Sim*</td>
            <td className="px-3 py-2">11 dígitos (* ou cnpj, nunca os dois)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.cnpj</td>
            <td className="px-3 py-2">Sim*</td>
            <td className="px-3 py-2">14 dígitos (* ou cpf)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.uf</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">UF do destinatário</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.email</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Armazenado cifrado (PII)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">destinatario.logradouro</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Endereço (PII cifrado)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[]</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">Pelo menos 1 item (máx. 500)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].descricao</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">Descrição do produto</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].ncm</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">8 dígitos</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].cfop</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">4 dígitos (ex. venda interna SP 5102)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].quantidade</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">Maior que zero</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].valor_unitario</td>
            <td className="px-3 py-2">Sim</td>
            <td className="px-3 py-2">Valor unitário (≥ 0)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].codigo</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">cProd no XML (padrão = índice do item)</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">itens[].unidade</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Padrão UN</td>
          </tr>
          <tr>
            <td className="px-3 py-2 font-mono text-xs">complemento</td>
            <td className="px-3 py-2">Não</td>
            <td className="px-3 py-2">Reservado; ignorado pelo motor simplificado</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 className="mt-6 text-sm font-semibold text-slate-900">Exemplo de body (homologação, 2 itens)</h3>
    <CodeBlock>{emitJsonExample}</CodeBlock>

    <h3 className="mt-6 text-sm font-semibold text-slate-900">Exemplo produção (destinatário CNPJ)</h3>
    <CodeBlock>{emitJsonCnpjExample}</CodeBlock>

    <h3 className="mt-6 text-sm font-semibold text-slate-900">Resposta 202 (imediata)</h3>
    <CodeBlock>{emitJsonResponseExample}</CodeBlock>
    <p className="mt-2 text-xs text-slate-500">
      Status possíveis depois do worker: ENFILEIRADO → PROCESSANDO → AUTORIZADO | REJEITADO | FALHA_COMUNICACAO | ERRO. REJEITADO traz codigo_status_sefaz e motivo_status_sefaz da SEFAZ. FALHA_COMUNICACAO não tem código. Campos{" "}
      <code className="rounded bg-slate-100 px-1">chave_acesso</code>, <code className="rounded bg-slate-100 px-1">protocolo</code>,{" "}
      <code className="rounded bg-slate-100 px-1">codigo_status_sefaz</code> preenchem após a SEFAZ.
    </p>

    <h3 className="mt-6 text-sm font-semibold text-slate-900">curl — emitir</h3>
    <CodeBlock>{`curl -sS -X POST \\
  -H "X-Api-Key: SUA_CHAVE" \\
  -H "Content-Type: application/json" \\
  "${base}/v1/empresas/{empresa_id}/nfe" \\
  -d @corpo.json`}</CodeBlock>
    <p className="mt-2 text-xs text-slate-500">Salve o JSON de exemplo em <code className="rounded bg-slate-100 px-1">corpo.json</code> ou use <code className="rounded bg-slate-100 px-1">-d &apos;...&apos;</code> em uma linha.</p>

    <h3 className="mt-6 text-sm font-semibold text-slate-900">curl — acompanhar emissão</h3>
    <CodeBlock>{`# Por id retornado no POST
curl -sS -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/nfe/{documento_id}"

# Depois de autorizada, por chave de 44 dígitos
curl -sS -H "X-Api-Key: SUA_CHAVE" \\
  "${base}/v1/empresas/{empresa_id}/nfe/chave/{chave44}"`}</CodeBlock>
  </section>
)

export const DesenvolvedoresPage = () => {
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <Link to="/" className="text-sm font-medium text-brand-600 hover:underline">
          ← Voltar
        </Link>
        <h1 className="mt-6 text-3xl font-bold text-slate-900">API {config.appName}</h1>
        <p className="mt-2 text-sm text-slate-600">
          Referência para integradores. Substitua <code className="rounded bg-slate-100 px-1">{`{empresa_id}`}</code> e{" "}
          <code className="rounded bg-slate-100 px-1">SUA_CHAVE</code>. Manifestação do destinatário (DF-e recebida) não faz
          parte desta API de emitente.
        </p>

        <div className="mt-10 space-y-10">
          {sections.slice(0, 4).map((sec) => (
            <section key={sec.title}>
              <h2 className="text-lg font-semibold text-slate-900">{sec.title}</h2>
              {sec.body ? <p className="mt-2 text-sm text-slate-600">{sec.body}</p> : null}
              {sec.curl ? <CodeBlock>{sec.curl}</CodeBlock> : null}
            </section>
          ))}

          <EmitNfeJsonSection />

          {sections.slice(4).map((sec) => (
            <section key={sec.title}>
              <h2 className="text-lg font-semibold text-slate-900">{sec.title}</h2>
              {sec.body ? <p className="mt-2 text-sm text-slate-600">{sec.body}</p> : null}
              {sec.curl ? <CodeBlock>{sec.curl}</CodeBlock> : null}
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-slate-600">
          Referência interativa (OpenAPI):{" "}
          <a href={config.apiDocsUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 hover:underline">
            Swagger /docs
          </a>
          {" "}
          — schema <strong>NfeEmitRequest</strong> no POST <strong>/v1/empresas/{`{empresa_id}`}/nfe</strong>.
        </p>
      </div>
    </main>
  )
}
